import { useCallback } from "react";
import { notification, message } from "antd";
import localforage from "localforage";

export function useBusinessLogic(API_URL, state) {
  const {
    setLoading,
    setTypeOfBusinesses,
    setQueueData,
    setBusinessDetails,
    setDetailsModal,
    selectedBusinessType,
    currentRadius,
    currentLocation,
  } = state;

  const fetchBusinessTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/getAllBusinessTypes`);
      const data = await response.json();
      if (response.ok) {
        const businessTypes = data.businessTypes.map((type) => ({
          title: type.charAt(0).toUpperCase() + type.slice(1),
          value: type,
        }));
        setTypeOfBusinesses(businessTypes);
      }
    } catch (error) {
      console.error("Error fetching business types:", error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, setLoading, setTypeOfBusinesses]);

  const fetchNearbyBusinesses = useCallback(
    async (location, businessType, setFavoritesEnabled) => {
      const businessTypeForData = businessType || selectedBusinessType;
      if (setFavoritesEnabled) {
        setFavoritesEnabled(false);
        await localforage.setItem("favorites_enable", false);
      }
      if (businessTypeForData) {
        setLoading(true);
        try {
          const token = await localforage.getItem("access_token");
          const response = await fetch(`${API_URL}/getNearbyBusinesses`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token ? `Bearer ${token}` : undefined,
            },
            body: JSON.stringify({
              businessType: businessTypeForData,
              radius: (await localforage.getItem("radius")) || currentRadius,
              location,
            }),
          });
          const data = await response.json();
          if (data.error) {
            notification.error({ message: data.error, duration: 1 });
          } else {
            setQueueData(data.nearbyBusinesses || []);
            if (data?.nearbyBusinesses?.length === 0) {
              message.error(`No nearby ${businessTypeForData}s available`);
            }
          }
        } catch (error) {
          notification.error({
            message: `Error fetching nearby ${businessTypeForData}s`,
          });
          console.error(
            `Error fetching nearby ${businessTypeForData}s:`,
            error
          );
        } finally {
          setLoading(false);
        }
      }
    },
    [API_URL, currentRadius, selectedBusinessType, setLoading, setQueueData]
  );

  const getFavorites = useCallback(
    async (setFavoritesEnabled = () => {}) => {
      const userIds = (await localforage.getItem("favorites_userIds")) || [];
      if (!userIds.length) {
        notification.warning({
          message:
            `No favorites have been added yet for ${selectedBusinessType}. Please click the star icon on the cards to add one.`,
        });
        return;
      }
      setFavoritesEnabled(true);
      await localforage.setItem("favorites_enable", true);
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/getFavorites`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userIds,
            businessType: selectedBusinessType,
          }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          if (!data.favorites.length) {
            notification.warning({
              message:
                `No favorites have been added yet for ${selectedBusinessType}. Please click the star icon on the cards to add one.`,
            });
            setFavoritesEnabled(false);
            await localforage.setItem("favorites_enable", false);
            fetchNearbyBusinesses(currentLocation)
            return
          }
          setQueueData(data.favorites || []);
        }
      } catch (error) {
        notification.error({ message: "Error fetching search results" });
      } finally {
        setLoading(false);
      }
    },
    [API_URL, selectedBusinessType, currentLocation, fetchNearbyBusinesses, setLoading, setQueueData]
  );

  const handleSearch = useCallback(
    async (searchQuery, setFavoritesEnabled) => {
      if (searchQuery.trim() === "") {
        return;
      }

      setFavoritesEnabled(false);
      await localforage.setItem("favorites_enable", false);

      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/getSearch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            searchQuery,
            businessType: selectedBusinessType,
          }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          setQueueData(data.searchResults || []);
        }
      } catch (error) {
        notification.error({ message: "Error fetching search results" });
      } finally {
        setLoading(false);
      }
    },
    [API_URL, selectedBusinessType, setLoading, setQueueData]
  );

  const openDetailsModal = useCallback(
    async (userId, setDetailsLoading) => {
      try {
        const response = await fetch(`${API_URL}/getBusinessDetails`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          setBusinessDetails(data.businessDetails);
        }
      } catch (error) {
        notification.error({ message: "Something went wrong!" });
        console.error("Something went wrong:", error);
      } finally {
        setDetailsModal(true);
        if (setDetailsLoading) setDetailsLoading(false);
      }
    },
    [API_URL, setBusinessDetails, setDetailsModal]
  );

  const refreshBusiness = useCallback(
    async (userId, setRefreshLoading) => {
      try {
        const response = await fetch(`${API_URL}/getBusinessDetails`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId }),
        });
        const data = await response.json();
        if (data.error) {
          notification.error({ message: data.error });
        } else {
          setQueueData((prevQueueData) =>
            prevQueueData.map((queue) => {
              if (queue.userId === userId) {
                const { queueNumber, isActive, businessMessage } =
                  data.businessDetails || {};
                return { ...queue, queueNumber, isActive, businessMessage };
              }
              return queue;
            })
          );
        }
      } catch (error) {
        notification.error({ message: "Something went wrong!" });
        console.error("Something went wrong:", error);
      } finally {
        if (setRefreshLoading) setRefreshLoading(false);
      }
    },
    [API_URL, setQueueData]
  );

  return { fetchBusinessTypes, fetchNearbyBusinesses, getFavorites, handleSearch, openDetailsModal, refreshBusiness };
}
